import { Radio, TextField } from '@hulu/react-style-components';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import React, { Fragment, useRef } from 'react';

import { Frequency } from '../../../../../apis/graphql/types';
import {
  BadgeList,
  BadgeProps,
  RadioWrapper,
  SideBarFooter,
  StyledTextFieldWrapper,
} from '../../../../../components/BlueTheme';
import MultipleSelect from '../../../../../components/BlueTheme/MultiSelect';
import { StyledCheckbox } from '../../../../../components/BlueTheme/StyledCheckbox/styled';
import {
  allWeekDays,
  DAYS_FIELD,
  EMAIL_NOW_FIELD,
  FORMAT_FIELD,
  formatOptions,
  FREQUENCY_FIELD,
  frequencyOptions,
  NAME_FIELD,
  ReportFormValidationSchema,
  reportInitialValues,
  SCHEDULE_REPORT_FIELD,
} from './constants';
import { SecondaryUsers } from './SecondaryUsers';
import {
  CheckboxContainer,
  DaysSelectorContainer,
  FieldContainerStyled,
  FormSideBarFooterWrapper,
  FrequencyContainer,
  HintWrapper,
  Label,
  ReportFormContainer,
  ScheduleReportDescription,
  TextFieldContainerStyled,
} from './styled';
import { ReportFormValues } from './types';

type Props = {
  initialValues?: ReportFormValues;
  handleSubmitReport: (input: ReportFormValues) => void;
  disabled?: boolean;
  onCancel: () => void;
  badges: BadgeProps[];
  buttonText: string;
  newReport?: boolean;
  campaignIds: string[];
};

const ReportForm = ({
  initialValues = reportInitialValues,
  disabled,
  handleSubmitReport,
  onCancel,
  badges,
  buttonText,
  newReport,
  campaignIds,
}: Props): JSX.Element => {
  const reportFormRef = useRef<FormikProps<ReportFormValues>>(null);
  const onCreateReport = (): void => {
    if (!reportFormRef.current) return;
    handleSubmitReport(reportFormRef.current.values);
  };

  return (
    <ReportFormContainer data-testid="reportFormContainer">
      <ScheduleReportDescription>
        Reports include all campaign activity to date. They will be sent to your email and updated in your Scheduled
        Reports list, when data is ready and available.
      </ScheduleReportDescription>
      <Formik<ReportFormValues>
        initialValues={initialValues}
        onSubmit={onCreateReport}
        validationSchema={ReportFormValidationSchema}
        innerRef={reportFormRef}
      >
        {({ isValid, dirty, setFieldValue }): JSX.Element => (
          <Form data-testid="reportForm">
            <TextFieldContainerStyled data-testid="fieldReportNameWrapper">
              <Field name={NAME_FIELD}>
                {({ field, meta }: FieldProps<string, ReportFormValues>): JSX.Element => (
                  <StyledTextFieldWrapper>
                    <TextField
                      {...field}
                      label="Report Name"
                      errMsg={meta.error}
                      placeholder="Enter report name"
                      data-testid="fieldReportName"
                    />
                  </StyledTextFieldWrapper>
                )}
              </Field>
            </TextFieldContainerStyled>
            <FieldContainerStyled>
              <BadgeList badges={badges} />
            </FieldContainerStyled>
            <FieldContainerStyled data-testid="fieldFormatWrapper">
              <Field name={FORMAT_FIELD}>
                {({ field }: FieldProps<string, ReportFormValues>): JSX.Element => (
                  <>
                    <Label>Format</Label>
                    {formatOptions.map((format) => (
                      <RadioWrapper key={format.value}>
                        <Radio
                          {...field}
                          label={format.label}
                          isChecked={field.value === format.value}
                          isErr={false}
                          value={format.value}
                          data-testid={`fieldFormat${format.value}`}
                        />
                      </RadioWrapper>
                    ))}
                  </>
                )}
              </Field>
            </FieldContainerStyled>
            <div data-testid="fieldEmailNowWrapper">
              <Field name={EMAIL_NOW_FIELD}>
                {({ field }: FieldProps<string, ReportFormValues>): JSX.Element => (
                  <>
                    <Label>Delivery</Label>
                    <CheckboxContainer>
                      <StyledCheckbox
                        {...field}
                        isChecked={!!field.value}
                        label="Email Now"
                        data-testid="fieldEmailNow"
                      />
                    </CheckboxContainer>
                  </>
                )}
              </Field>
            </div>
            <div data-testid="fieldScheduleReportWrapper">
              <Field name={SCHEDULE_REPORT_FIELD}>
                {({ field: scheduleReportField }: FieldProps<string, ReportFormValues>): JSX.Element => (
                  <>
                    <CheckboxContainer>
                      <StyledCheckbox
                        {...scheduleReportField}
                        isChecked={!!scheduleReportField.value}
                        label="Schedule a recurring report"
                        data-testid="fieldScheduleReport"
                      />
                    </CheckboxContainer>
                    {scheduleReportField.value && (
                      <Field name={FREQUENCY_FIELD}>
                        {({ field: frequencyField }: FieldProps<string, ReportFormValues>): JSX.Element => (
                          <FrequencyContainer data-testid="frequencyWrapper">
                            {frequencyOptions.map((frequency) => (
                              <Fragment key={frequency.value}>
                                <RadioWrapper>
                                  <Radio
                                    {...frequencyField}
                                    label={frequency.label}
                                    isChecked={frequencyField.value === frequency.value}
                                    isErr={false}
                                    value={frequency.value}
                                    data-testid={`fieldFrequency${frequency.value}`}
                                  />
                                  {frequency.hint && frequencyField.value === frequency.value && (
                                    <HintWrapper>{frequency.hint}</HintWrapper>
                                  )}
                                </RadioWrapper>
                                {frequency.value === Frequency.Weekly && frequencyField.value === Frequency.Weekly && (
                                  <Field name={DAYS_FIELD}>
                                    {({
                                      field: { value: daysFieldValue },
                                      meta: { error },
                                    }: FieldProps<string[], ReportFormValues>): JSX.Element => {
                                      return (
                                        <DaysSelectorContainer data-testid="daysSelectorWrapper">
                                          <MultipleSelect
                                            loading={false}
                                            allItems={allWeekDays}
                                            initiallySelected={daysFieldValue}
                                            maxSelectedItemsCount={100}
                                            total={7}
                                            onChange={(selectedDays): void => {
                                              setFieldValue(
                                                DAYS_FIELD,
                                                selectedDays.map((selectedDay) => selectedDay.value)
                                              );
                                            }}
                                            handleSearch={(): void => {}}
                                            onNextPage={(): void => {}}
                                            showSelectionAndSearchBlock={false}
                                            errorMessage={error}
                                          />
                                        </DaysSelectorContainer>
                                      );
                                    }}
                                  </Field>
                                )}
                              </Fragment>
                            ))}
                          </FrequencyContainer>
                        )}
                      </Field>
                    )}
                  </>
                )}
              </Field>
              <SecondaryUsers campaignIds={campaignIds} />
            </div>
            <FormSideBarFooterWrapper>
              <SideBarFooter
                disabled={disabled || (newReport && !dirty) || !isValid}
                name={buttonText}
                onCancel={onCancel}
              />
            </FormSideBarFooterWrapper>
          </Form>
        )}
      </Formik>
    </ReportFormContainer>
  );
};

export default ReportForm;
